import React, { useState } from 'react';
import { 
  User, 
  Bell, 
  Shield, 
  Palette,
  Save
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';

export default function TherapistSettings() {
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      toast.success('Settings saved successfully');
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your profile and preferences</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      
      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" className="gap-2">
            <User className="w-4 h-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="ai" className="gap-2">
            <Shield className="w-4 h-4" />
            AI Preferences
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="profile" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                <Avatar className="w-20 h-20">
                  <AvatarImage src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200" />
                  <AvatarFallback>LC</AvatarFallback>
                </Avatar>
                <div>
                  <Button variant="outline" size="sm">Change Photo</Button>
                  <p className="text-xs text-muted-foreground mt-2">JPG, PNG or GIF. Max 2MB.</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input defaultValue="Dr. Lisa Chen" className="mt-1.5" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input defaultValue="lisa.chen@physiomobile.com" className="mt-1.5" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input defaultValue="+60 12-345 6789" className="mt-1.5" />
                </div>
                <div>
                  <Label>Specialization</Label>
                  <Select defaultValue="physio">
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="physio">Physiotherapy</SelectItem>
                      <SelectItem value="ot">Occupational Therapy</SelectItem>
                      <SelectItem value="speech">Speech Therapy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Bio</Label>
                <Input 
                  defaultValue="Senior Physiotherapist with 10+ years of experience in sports rehabilitation and post-surgical recovery." 
                  className="mt-1.5" 
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="notifications" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
                { label: 'Session reminders', description: 'Get notified 15 minutes before sessions', defaultChecked: true },
                { label: 'AI insights', description: 'Receive AI recommendations for your patients', defaultChecked: true },
                { label: 'Patient messages', description: 'Notifications when patients send messages', defaultChecked: true },
                { label: 'Compliance alerts', description: 'Alert when patients miss exercises', defaultChecked: true },
                { label: 'Weekly reports', description: 'Summary of your caseload performance', defaultChecked: false },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div>
                    <Label>{item.label}</Label>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                  <Switch defaultChecked={item.defaultChecked} />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="ai" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Assistant Preferences</CardTitle>
              <CardDescription>Configure how AI assists you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Show AI recommendations</Label>
                  <p className="text-sm text-muted-foreground">Display AI suggestions on patient profiles</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-summarize sessions</Label>
                  <p className="text-sm text-muted-foreground">Generate AI summaries after each session</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <Separator />
              
              <div>
                <Label>AI suggestion confidence threshold</Label>
                <p className="text-sm text-muted-foreground mb-2">Only show suggestions above this confidence level</p>
                <Select defaultValue="70">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50% and above</SelectItem>
                    <SelectItem value="60">60% and above</SelectItem>
                    <SelectItem value="70">70% and above</SelectItem>
                    <SelectItem value="80">80% and above</SelectItem>
                    <SelectItem value="90">90% and above</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Show disclaimer on AI responses</Label>
                  <p className="text-sm text-muted-foreground">Display "clinician decides" reminder</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
